import React, { type ReactNode } from 'react';

import { Button } from '@pkg/components/Sections/Profile/Buttons';
import { Sizes } from '@pkg/components/Sections/Profile/Buttons/Button/types';
import { Colors, Variants } from '@pkg/components/Sections/Profile/Buttons/ButtonBase';
import { useMediaQuery } from '@pkg/providers';

import { ActionBannerWrapper, ChildrenWrapper, Interior, Prefix, PrefixWrapper, SuffixWrapper } from './styles';

interface SuffixProps {
    actionContent: string;
    ariaLabel?: string;
    disabled?: boolean;
    success?: boolean;
    onAction?(): void;
}

export interface ActionBannerProps extends SuffixProps {
    children: ReactNode;
    // Consider an optional `prefix` prop to accept
    // a custom emoji (related to "status" type of the Banner).
    suffix?: boolean;
}

export function ActionBanner({ children, suffix = true, ...suffixProps }: ActionBannerProps) {
    const suffixMarkup = suffix && <Suffix {...suffixProps} />;
    return (
        <ActionBannerWrapper>
            <Interior>
                <PrefixWrapper>
                    <Prefix>🎁</Prefix>
                </PrefixWrapper>

                <ChildrenWrapper>{children}</ChildrenWrapper>
                {suffixMarkup}
            </Interior>
        </ActionBannerWrapper>
    );
}

export function Suffix({ actionContent, ariaLabel, disabled = false, success = false, onAction }: SuffixProps) {
    const { tablet } = useMediaQuery();

    const isDisabled = disabled || !onAction;

    return (
        <SuffixWrapper>
            <Button
                aria-label={ariaLabel}
                disabled={isDisabled}
                color={getSuffixColor({ disabled: isDisabled, success })}
                variant={getSuffixVariant({ disabled: isDisabled })}
                size={tablet ? Sizes.small : Sizes.medium}
                onClick={onAction}
            >
                {actionContent}
            </Button>
        </SuffixWrapper>
    );
}

export function getSuffixColor({ disabled, success }: Pick<SuffixProps, 'disabled' | 'success'>) {
    if (disabled) return Colors.secondary;

    return success ? Colors.success : Colors.primary;
}

export function getSuffixVariant({ disabled }: Pick<SuffixProps, 'disabled'>) {
    return disabled ? Variants.solid : Variants.yat;
}
